import { linkOptions, type LinkOptions } from '@tanstack/react-router';

import type { AttentionRow, BoardStateFilter } from '@/features/tasks';
import type { FileChangeEventView } from '@/lib/api/generated/model';
import { parentFolderOf } from '@/features/workspace-files';

/**
 * Where a row on Overview takes you.
 *
 * One rule per kind, in one place, because the rules are the sort that drift:
 * "the failure is on the run history, everything else on the details" is
 * invisible once it is spread across five components, and a row that opened the
 * wrong tab would look like it worked.
 */

/**
 * A task's detail on its own board.
 *
 * `null` when the task's workflow has no board — a draft has no committed
 * definition, so this would otherwise point at an address the board route
 * answers with not-found. A row that cannot be opened says so by not being a
 * link, rather than by disappointing whoever clicks it.
 */
export function attentionRowLink(workspaceId: string, row: AttentionRow) {
  if (!row.hasBoard) return null;

  return linkOptions({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
    params: { workspaceId, workflowKey: row.workflowKey },
    search: {
      task: row.taskId,
      // A failure and a stop are both recorded on a run, so those rows open
      // on the Runs tab.
      // Every other kind says where the task is standing, which the Details
      // tab answers.
      tab:
        row.kind === 'failed-run' || row.kind === 'stopped'
          ? ('executions' as const)
          : ('details' as const),
    },
  });
}

/**
 * A task's run page (spec 115): where a parent's subtasks are, as a board of
 * their own. The breadcrumb on a subtask row leads here (spec 150 FR-005).
 */
export function runPageLink(
  workspaceId: string,
  workflowKey: string,
  taskId: string,
) {
  return linkOptions({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/tasks/$taskId/run',
    params: { workspaceId, workflowKey, taskId },
  });
}

/**
 * A whole board, narrowed to one state — where a capped group's overflow link
 * goes, since the board is the exhaustive surface Overview deliberately is not.
 */
export function boardStateLink(
  workspaceId: string,
  workflowKey: string,
  state: BoardStateFilter,
) {
  return linkOptions({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
    params: { workspaceId, workflowKey },
    search: { state },
  });
}

/**
 * A changed file, open in the file browser.
 *
 * The browser identifies a file by its full path and browses by its folder, so
 * a link has to carry both: `file` selects it, `path` is the listing behind it.
 * Landing on the right file with the wrong folder open would show the preview
 * over a directory the file is not in.
 *
 * A deletion selects nothing. The file is gone, so `file` would name a path the
 * browser cannot open — the same "link that disappoints whoever clicks it" the
 * attention rows refuse for a draft's task. The folder it was deleted from is
 * the honest destination, and it is the one a reader wants next anyway.
 *
 * A move records the *destination* on `path` (the origin is `source_path`), so
 * this opens where the file now is, which is where a reader following the row
 * expects to arrive.
 */
export function fileChangeLink(
  workspaceId: string,
  // Only the two fields a destination depends on. The results tab (spec 095)
  // passes a `TaskFileChangeView`, which has these and not the rest.
  event: Pick<FileChangeEventView, 'path' | 'event_type'>,
  /*
    Where "open this file" goes, decided by the caller (spec 140 FR-050).

    This module is a plain module, not a component, so it cannot read the `sm`
    breakpoint `useWorkspaceFilePreviewLink` subscribes to. Rather than grow a
    second `matchMedia` subscription here — which is exactly the duplication
    SC-020 measures — the calling component passes the decision in.

    Required, not optional with a default: a default would silently give a
    caller the navigate-away behaviour this feature exists to replace, and it
    would be wrong in precisely the case nobody would notice.
  */
  previewLinkTo: (path: string) => LinkOptions,
): LinkOptions {
  // A deletion selects nothing, so there is nothing to preview either: the
  // folder it was deleted from is the destination, and that is the file
  // browser, never the panel.
  if (event.event_type === 'deleted') {
    return linkOptions({
      to: '/workspaces/$workspaceId/files',
      params: { workspaceId },
      search: {
        path: parentFolderOf(event.path),
        file: undefined,
        // Always the preview, never the history: the reader has just come from
        // a list of changes, so opening another one answers a question they
        // did not ask. The file's own history is one tab away once they are
        // there.
        view: 'preview' as const,
      },
    });
  }

  return previewLinkTo(event.path);
}
